'use client';

import { SvgChevronright } from '@common/icon';
import { cn } from '@core/utils';
import { CustomLink } from '@/app/_components/CustomLink';
import { isExternalHref, MENU_CATEGORIES, type MenuItem } from './MenuItems';
import { WelloPointMenuActionItem } from './WelloPointMenuActionItem';
type MyPageMenuSectionsVariant = 'mobile' | 'desktop';
const variantClass = {
  mobile: {
    container: 'relative rounded-t-large bg-Gray-white px-20 pb-64 pt-32',
    sectionGap: 'flex flex-col gap-28',
    divider: '-mx-20 mb-28 border-t border-Gray-200',
    item: 'group -mx-8 flex min-h-48 items-center rounded-small px-8 py-12 transition-colors duration-200 ease-out hover:bg-Gray-50 active:bg-Gray-100',
    detailGap: 'gap-12',
    baseGap: 'gap-8',
    iconBox: 'flex size-48 shrink-0 items-center justify-center rounded-large bg-Gray-50',
    icon: 'size-24',
    title: 'mb-12 font-700 text-Gray-900 typo-body3',
    label: 'truncate font-500 text-Gray-900 typo-body3',
    description: 'truncate font-600 text-Gray-550 typo-caption1',
    chevron: 'size-16 shrink-0 text-Gray-600 transition-transform duration-200 ease-out group-active:translate-x-4'
  },
  desktop: {
    container: 'bg-Gray-white px-32 pb-32 pt-28',
    sectionGap: 'flex flex-col gap-28',
    divider: 'mb-28 border-t border-Gray-200',
    item: 'group flex min-h-48 items-center rounded-small py-12 transition-colors duration-200 ease-out hover:bg-Gray-50',
    detailGap: 'gap-12',
    baseGap: 'gap-8',
    iconBox: 'flex size-24 shrink-0 items-center justify-center',
    icon: 'size-24',
    title: 'mb-12 font-700 text-Gray-900 typo-body3',
    label: 'truncate font-500 text-Gray-900 typo-body3',
    description: 'truncate font-500 text-Gray-550 typo-caption1',
    chevron: 'size-16 shrink-0 text-Gray-500 transition-transform duration-200 ease-out group-hover:translate-x-2'
  }
} as const;
export const MyPageMenuSections = ({
  visibleMenuItems,
  isReady,
  variant = 'mobile'
}: {
  visibleMenuItems: readonly MenuItem[];
  isReady: boolean;
  variant?: MyPageMenuSectionsVariant;
}) => {
  const classes = variantClass[variant];
  let globalIndex = 0;
  return <div className={classes.container}>
      <div className={cn(classes.sectionGap, isReady ? 'duration-300 animate-in fade-in' : 'opacity-0')}>
        {MENU_CATEGORIES.map(category => {
        const items = visibleMenuItems.filter(item => item.category === category.key);
        if (items.length === 0) return null;
        const hasDetailItems = items.some(item_0 => item_0.description);
        return <section key={category.key}>
              {category.key === 'service-link' && <hr className={classes.divider} />}
              <h2 className={classes.title}>{category.label}</h2>
              <ul>
                {items.map(item_1 => {
              const delay = globalIndex++ * 30;
              const isExternal = isExternalHref(item_1.href);
              return <li key={item_1.id} className={cn(isReady && 'duration-300 animate-in fade-in fill-mode-both')} style={isReady ? {
                animationDelay: `${delay}ms`
              } : undefined}>
                      {item_1.actionType === 'wello-point-receive' ? <WelloPointMenuActionItem item={item_1} hasDetailItems={hasDetailItems} itemClassName={classes.item} iconBoxClassName={classes.iconBox} iconClassName={classes.icon} labelClassName={classes.label} detailGapClassName={classes.detailGap} baseGapClassName={classes.baseGap} /> : <CustomLink href={item_1.href} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noopener noreferrer' : undefined} className={cn(classes.item, hasDetailItems ? classes.detailGap : classes.baseGap)}>
                          {hasDetailItems ? <span className={classes.iconBox}>
                              <item_1.Icon originalFill className={cn(classes.icon, 'shrink-0')} />
                            </span> : <item_1.Icon originalFill className={cn(classes.icon, 'shrink-0')} />}
                          <span className="flex min-w-0 grow flex-col gap-2">
                            <span className={classes.label}>{item_1.label}</span>
                            {item_1.description && <span className={classes.description}>
                                {item_1.description}
                              </span>}
                          </span>
                          {(item_1.description || isExternal) && <SvgChevronright aria-hidden="true" className={classes.chevron} />}
                        </CustomLink>}
                    </li>;
            })}
              </ul>
            </section>;
      })}
      </div>
    </div>;
};